import mixpanel from 'mixpanel-browser'

/**
 * Camada central de tracking de produto (SC-10694).
 *
 * Antes deste arquivo, cada componente chamava `mixpanel.track` direto com o
 * boilerplate `mixpanel as unknown as { __loaded: string }` repetido em ~24
 * lugares, e os nomes eram frases livres em português ("Modal Cobranças com
 * falha insights oqpa"). O resultado media navegação: 94% do volume era um
 * único evento de clique em menu, e nenhuma jornada de valor era registrada.
 *
 * A medição no banco de produção mostrou o custo disso: das 159 contas criadas
 * em agosto, 84 (53%) pararam na primeira tela e 94 de 96 nunca fizeram um
 * segundo login. Nada disso aparecia no Mixpanel — só em query de MySQL.
 *
 * Regras deste módulo:
 * - Nome de evento é união de tipos, `snake_case`, verbo no passado.
 * - Toda chamada é protegida: telemetria nunca quebra fluxo de negócio.
 * - Nada de PII. E-mail entra como domínio, valores como faixa quando possível.
 */

export type TrackEvent =
  // Ativação — os marcos que hoje só existem via SQL
  | 'first_login'
  | 'onboarding_step_completed'
  | 'customer_created'
  | 'charge_created'
  | 'recurrence_created'
  // Primeira sessão — 94 de 96 que abandonam nunca voltam, então só ela conta
  | 'page_viewed'
  | 'welcome_modal_viewed'
  | 'welcome_modal_skipped'
  | 'welcome_modal_completed'
  // Paywall — funil de receita, hoje totalmente invisível
  | 'paywall_blocked'
  | 'upgrade_modal_viewed'
  | 'upgrade_cta_clicked'
  // Checkout do plano dentro do CONTA (SC-11070)
  | 'saas_checkout_started'
  | 'saas_checkout_payment_method'
  | 'saas_checkout_paid'
  | 'saas_checkout_setup_toggled'
  // Falhas
  | 'form_validation_failed'
  | 'api_error'

type TrackProps = Record<
  string,
  string | number | boolean | string[] | undefined | null
>

/** Dispara um evento. Silencioso em caso de falha, por design. */
export const track = (event: TrackEvent, props: TrackProps = {}) => {
  try {
    mixpanel.track(event, props)
  } catch {
    // Telemetria nunca pode quebrar o fluxo do usuário.
  }
}

/**
 * Propriedades enviadas em TODOS os eventos seguintes (super properties).
 *
 * Sem isto não havia como cruzar comportamento com plano ou canal — nenhum
 * `mixpanel.register()` existia no projeto. É justamente o corte necessário
 * para responder se a queda de ativação (17,3% para 7,5% entre junho e agosto)
 * é qualidade de mídia ou problema de produto.
 */
export const registerSuperProperties = (props: {
  merchant_uuid?: string
  plan?: string
  role?: string
  merchant_stage?: string
  // SC-10813: o UTM da URL só existe na tela de cadastro, então nenhum evento
  // depois do login carregava origem — 92% dos `first_login` chegavam sem
  // canal, e a pergunta "a queda é de um canal específico?" só era
  // respondível por query no banco. Vem do payload de login, não da URL.
  acquisition_source?: string
  acquisition_medium?: string
  acquisition_campaign?: string
}) => {
  try {
    const clean = Object.fromEntries(
      Object.entries(props).filter(([, v]) => v !== undefined && v !== null)
    )
    if (Object.keys(clean).length) mixpanel.register(clean)
  } catch {
    // idem
  }
}

/**
 * Faixa em vez do valor exato: permite analisar ticket sem jogar dado
 * financeiro do merchant no Mixpanel.
 */
export const amountRange = (amount?: number | null): string | undefined => {
  if (typeof amount !== 'number' || Number.isNaN(amount)) return undefined
  if (amount < 50) return '0-50'
  if (amount < 200) return '50-200'
  if (amount < 500) return '200-500'
  if (amount < 1000) return '500-1000'
  if (amount < 5000) return '1000-5000'
  return '5000+'
}

/**
 * Largura de viewport, não user agent — é como o GA4 separa mobile de desktop,
 * e o funil de cadastro mostrou o mobile convertendo 21,4% contra 34,6%.
 */
export const isMobileViewport = (): boolean | undefined =>
  typeof window === 'undefined' ? undefined : window.innerWidth < 768
